import { Injectable } from '@angular/core';
import { SupabaseClient } from '@supabase/supabase-js';

import { SupabaseService } from '../../../core/services/supabase.service';
import { MealsRepository } from '../domain/meals.repository';
import { DailyMealValue, MealSlot } from '../models/meal.model';

@Injectable()
export class SupabaseMealsRepository extends MealsRepository {
  // Generated Supabase types require owner_id on inserts and mark nullable SQL RPC
  // parameters as non-null. The database owns those invariants, so this adapter keeps
  // the SDK client broad while its mapped rows remain explicitly typed.
  private readonly client: SupabaseClient;

  constructor(supabase: SupabaseService) {
    super();
    this.client = supabase.client;
  }

  async loadDate(date: string): Promise<MealSlot[]> {
    const { error: defaultsError } = await this.client.rpc('ensure_default_calendar_axes');
    if (defaultsError) throw defaultsError;

    const [axesResult, mealsResult] = await Promise.all([
      this.client
        .from('calendar_axes')
        .select('id,name,system_key,position,is_visible,is_archived')
        .eq('kind', 'MEAL')
        .order('position')
        .order('id'),
      this.client
        .from('daily_meals')
        .select('id,calendar_axis_id,meal')
        .eq('meal_date', date)
    ]);

    if (axesResult.error) throw axesResult.error;
    if (mealsResult.error) throw mealsResult.error;

    const axes = axesResult.data ?? [];
    const meals = mealsResult.data ?? [];
    const mealByAxis = new Map(meals.map(row => [row.calendar_axis_id, row.meal]));

    return axes
      .filter(axis => (!axis.is_archived && axis.is_visible) || mealByAxis.has(axis.id))
      .map(axis => ({
        axisId: axis.id,
        axisName: axis.name,
        systemKey: axis.system_key,
        position: axis.position,
        meal: mealByAxis.get(axis.id) ?? null
      }));
  }

  async loadRange(startDate: string, endDate: string): Promise<DailyMealValue[]> {
    const { data, error } = await this.client
      .from('daily_meals')
      .select('meal_date,calendar_axis_id,meal')
      .gte('meal_date', startDate)
      .lte('meal_date', endDate)
      .order('meal_date')
      .order('calendar_axis_id');
    if (error) throw error;

    return (data ?? []).map(row => ({
      mealDate: row.meal_date,
      axisId: row.calendar_axis_id,
      meal: row.meal
    }));
  }

  async loadSuggestions(): Promise<string[]> {
    const { data, error } = await this.client
      .from('daily_meals')
      .select('meal,updated_at')
      .order('updated_at', { ascending: false })
      .limit(200);
    if (error) throw error;

    const suggestions: string[] = [];
    const seen = new Set<string>();
    for (const row of data ?? []) {
      const meal = row.meal.trim();
      const key = meal.toLocaleLowerCase('fr');
      if (!meal || seen.has(key)) continue;
      seen.add(key);
      suggestions.push(meal);
      if (suggestions.length >= 50) break;
    }
    return suggestions;
  }

  async saveMeal(date: string, axisId: number, meal: string): Promise<void> {
    const normalized = meal.trim();
    if (!normalized) {
      await this.clearMeal(date, axisId);
      return;
    }

    const { data: existing, error: lookupError } = await this.client
      .from('daily_meals')
      .select('id')
      .eq('meal_date', date)
      .eq('calendar_axis_id', axisId)
      .maybeSingle();
    if (lookupError) throw lookupError;

    const query = existing
      ? this.client
          .from('daily_meals')
          .update({ meal: normalized })
          .eq('id', existing.id)
      : this.client
          .from('daily_meals')
          .insert({ meal_date: date, calendar_axis_id: axisId, meal: normalized });

    const { error } = await query;
    if (error) throw error;
  }

  async clearMeal(date: string, axisId: number): Promise<void> {
    const { error } = await this.client
      .from('daily_meals')
      .delete()
      .eq('meal_date', date)
      .eq('calendar_axis_id', axisId);
    if (error) throw error;
  }
}
